/* eslint-disable react/no-unused-prop-types */
/* eslint-disable react/require-default-props */
/* eslint-disable @typescript-eslint/no-unsafe-call */
/* eslint-disable @typescript-eslint/no-unsafe-assignment */
import React, { useState, ReactNode, useRef, useEffect } from 'react';
import classNames from 'classnames/bind';
import styles from './input.pcss';

const cx = classNames.bind(styles);

type Fn = () => void;

interface InputProps {
  id: string | number;
  label?: string;
  name?: string;
  prefix?: ReactNode;
  suffix?: ReactNode;
  maxLength?: number;
  onChange?: (arg0: string) => void;
  onFocus?: Fn;
  onBlur?: Fn;
  defaultValue?: string;
  readOnly?: boolean;
  validationError?: boolean;
  autoComplete?: 'on' | 'off';
  followText?: ReactNode | string;
  htmlType?: 'text' | 'email' | 'password' | 'number' | 'tel';
  luxe?: boolean | undefined;
  hasFromPdp?: boolean | undefined;
  hasFromAddAddress?: boolean | undefined;
  hasFromAddPincode?: boolean | undefined;
  onKeyPressed?: Fn;
  onKeyup?: Fn;
  fromLoginPage?: boolean | undefined;
  fromFleek?: boolean | undefined;
  tabIndex?: number;
  ariaLabel?: string;
  disabled?:boolean;
  required?: boolean;
  ariaLive?: 'polite' | 'assertive' | 'off';
  labelHidden?: boolean;
  ariaRequired?: boolean;
  ariaDisabled?:boolean;
  ariaLabelledBy?: string;
  numericOnly?: boolean;
  isGSTField?: boolean;
}

const useFocus = (): void => {
  const htmlElRef = useRef(null);
  const setFocus = (): void => {
    if (htmlElRef.current) htmlElRef.current.focus();
  };
  return [htmlElRef, setFocus];
};

const TextInput = (props: InputProps): JSX.Element => {
  const {
    label,
    name,
    maxLength,
    defaultValue,
    id,
    prefix,
    suffix,
    readOnly,
    followText,
    validationError,
    autoComplete = 'off',
    htmlType = 'text',
    luxe,
    hasFromPdp,
    hasFromAddAddress,
    hasFromAddPincode,
    onKeyPressed,
    onKeyup,
    fromLoginPage,
    fromFleek = false,
    tabIndex,
    ariaLabel,
    disabled,
    required = false,
    ariaLive = 'off',
    labelHidden=false,
    ariaRequired=false,
    ariaDisabled=false,
    ariaLabelledBy,
    numericOnly = false,
    isGSTField = false
  } = props;

  const isLuxeInput = luxe && (hasFromPdp || hasFromAddAddress || hasFromAddPincode);
  const [focus, setFocus] = useState(false);
  const [floatingLabel, setFloatingLabel] = useState(false);
  const [inputVal, setValue] = useState(defaultValue || '');
  const [inputRef, setInputFocus] = useFocus();

  useEffect(() => {
    if (defaultValue) {
      setValue(defaultValue);
      setFloatingLabel(true);
    }
    if (defaultValue === '') {
      setValue(defaultValue);
      setFloatingLabel(false);
    }
  }, [defaultValue]);

  useEffect(() => {
    if (fromLoginPage) {
      setValue('');
      setFloatingLabel(false);
    }
  }, [label, prefix]);

  const handleFocus = (): void => {
    const { onFocus } = props;
    if (!readOnly) {
      setFocus(true);
      setFloatingLabel(true);
    }
    if (onFocus) onFocus();
  };
  const handleBlur = (): void => {
    const { onBlur } = props;
    setFocus(false);
    if (!inputVal) {
      setFloatingLabel(false);
    }
    if (onBlur) onBlur();
  };

  const handleChange = (event: any): void => {
    const { onChange, numericOnly } = props;
    const {
      target: { value = '', name }
    } = event;
    const cleanedValue = numericOnly ? value.replace(/\D/g, '') : value;
    const regex = /^[0-9\b]+$/;
    let inputValue;

    if (
      isLuxeInput &&
      maxLength &&
      cleanedValue.length <= maxLength &&
      (cleanedValue === '' || regex.test(cleanedValue))
    ) {
      inputValue = cleanedValue;
    } else if (!isLuxeInput) {
      inputValue = cleanedValue;
    }
    if (inputValue !== undefined) {
      setValue(inputValue);
      if (onChange) onChange(inputValue, name);
    }
  };

  const inputClass: string = cx(fromFleek ? 'fleek-field' : 'field', {
    active: focus,
    floatingLabel,
    prefix: !fromLoginPage && prefix,
    mobilePrefix: prefix && fromLoginPage && (inputVal !== '' || focus),
    suffix,
    validationError: (focus || isGSTField) && validationError
  });

  const luxeInputClass: string = cx('field', 'luxe-field', {
    luxeActive: focus,
    floatingLabel,
    prefix: !fromLoginPage && prefix,
    mobilePrefix: focus && prefix && fromLoginPage && inputVal !== '',
    suffix,
    luxeValidationError: (focus || isGSTField) && validationError
  });

  return (
    <div aria-live={ariaLive}>
      <div className={isLuxeInput ? luxeInputClass : inputClass} onClick={setInputFocus}>
        {!fromLoginPage && prefix && <div className={cx('prefix')}>{prefix}</div>}
        {fromLoginPage &&
          (focus || inputVal !== '') &&
          prefix &&
          label === 'Enter Mobile Number' && <div className={cx('mobile-prefix')}>{prefix}</div>}
        <label htmlFor={id} className={isLuxeInput ? cx('bold-font') : ''} aria-hidden={labelHidden}>
          {floatingLabel ? <strong role="presentation">{label}</strong> : label}
        </label>
        <input
          id={id}
          autoFocus={id === "otp"}
          ref={inputRef}
          value={inputVal}
          onChange={handleChange}
          onFocus={handleFocus}
          onBlur={handleBlur}
          inputMode={numericOnly ? 'numeric' : undefined}
          pattern={numericOnly ? '[0-9]*' : undefined}
          type={htmlType}
          name={name}
          maxLength={maxLength}
          readOnly={readOnly}
          autoComplete={autoComplete}
          className={isLuxeInput ? 'luxe-input' : ''}
          onKeyPress={e => onKeyPressed && onKeyPressed(e)}
          onKeyUp={e => onKeyup && onKeyup(e)}
          // aria-label={ariaLabel ? ariaLabel : name}
          aria-label={
            labelHidden
              ? ariaLabel || label
              : (!ariaLabelledBy && ariaLabel ? ariaLabel : undefined)
          }
          aria-labelledby={
            !labelHidden && ariaLabelledBy ? ariaLabelledBy : undefined
          }
          tabIndex={tabIndex}
          disabled={disabled}
          required={required}
          aria-required={ariaRequired}
          aria-describedby={validationError ? `${id}-error` : undefined}
          aria-disabled={ariaDisabled}
        />
        {suffix && <div className={isLuxeInput ? cx('luxe-suffix') : cx('suffix')}>{suffix}</div>}
      </div>
      {followText && (
        <div aria-label={typeof followText === 'string' ? followText : ''}>
          {followText}
        </div>
      )}
    </div>
  );
};

export default TextInput;
